import React, { useEffect, useState } from 'react';
import { FileText, Download, Loader2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { downloadsAPI } from '../services/api';
import { toast } from 'sonner';

const formatBytes = (bytes) => {
  if (!bytes) return '0 KB';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const CATEGORY_LABELS = {
  de: {
    factsheet: 'Factsheet',
    report: 'Bericht',
    presentation: 'Präsentation',
    legal: 'Rechtliches',
    project: 'Projekt'
  },
  en: {
    factsheet: 'Factsheet',
    report: 'Report',
    presentation: 'Presentation',
    legal: 'Legal',
    project: 'Project'
  },
  ru: {
    factsheet: 'Факт-лист',
    report: 'Отчёт',
    presentation: 'Презентация',
    legal: 'Правовая информация',
    project: 'Проект'
  },
  vi: {
    factsheet: 'Bản thông tin',
    report: 'Báo cáo',
    presentation: 'Bài thuyết trình',
    legal: 'Pháp lý',
    project: 'Dự án'
  }
};

const SECTION_TEXT = {
  de: {
    title: 'Downloads',
    subtitle: 'Factsheets, Berichte und Unterlagen für Investoren',
    empty: 'Noch keine Downloads verfügbar.',
    download: 'Herunterladen',
    loading: 'Lädt...'
  },
  en: {
    title: 'Downloads',
    subtitle: 'Factsheets, reports and documents for investors',
    empty: 'No downloads available yet.',
    download: 'Download',
    loading: 'Loading...'
  },
  ru: {
    title: 'Загрузки',
    subtitle: 'Факт-листы, отчёты и документы для инвесторов',
    empty: 'Пока нет файлов для загрузки.',
    download: 'Скачать',
    loading: 'Загрузка...'
  },
  vi: {
    title: 'Tải xuống',
    subtitle: 'Bản thông tin, báo cáo và tài liệu cho nhà đầu tư',
    empty: 'Chưa có tài liệu nào.',
    download: 'Tải xuống',
    loading: 'Đang tải...'
  }
};

export const Downloads = () => {
  const { language } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const text = SECTION_TEXT[language] || SECTION_TEXT.de;
  const labels = CATEGORY_LABELS[language];

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await downloadsAPI.getPublic();
        if (mounted && res?.success) setItems(res.downloads || []);
      } catch (e) {
        toast.error(e.message || 'Error loading downloads');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleDownload = (item) => {
    const url = downloadsAPI.getDownloadUrl(item.id);
    window.open(url, '_blank', 'noopener');
  };

  return (
    <section id="downloads" className="py-24 bg-gradient-to-b from-black via-[#0a0a0a] to-black" data-testid="downloads-section">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-12">
          <div className="inline-block px-4 py-1 bg-[#C4A55A]/10 border border-[#C4A55A]/30 rounded-full mb-4">
            <span className="text-[#C4A55A] text-sm font-medium tracking-wide uppercase">
              {text.title}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {text.title}
          </h2>
          <p className="text-lg text-gray-400">{text.subtitle}</p>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-gray-400" data-testid="downloads-loading">
            <Loader2 className="h-5 w-5 animate-spin text-[#C4A55A]" />
            <span>{text.loading}</span>
          </div>
        ) : items.length === 0 ? (
          <p className="text-gray-500" data-testid="downloads-empty">{text.empty}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="downloads-list">
            {items.map((item) => (
              <Card
                key={item.id}
                data-testid={`download-card-${item.id}`}
                className="bg-[#1a1a1a] border-[#C4A55A]/20 hover:border-[#C4A55A]/60 transition-all duration-300 group"
              >
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="p-3 rounded-lg bg-[#C4A55A]/10 border border-[#C4A55A]/30">
                      <FileText className="h-6 w-6 text-[#C4A55A]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="inline-block px-2 py-0.5 text-xs rounded bg-[#C4A55A]/15 text-[#C4A55A] mb-2">
                        {labels[item.category] || item.category}
                      </span>
                      <h3 className="text-white font-semibold leading-snug truncate" title={item.title}>
                        {item.title}
                      </h3>
                    </div>
                  </div>

                  {item.description && (
                    <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                      {item.description}
                    </p>
                  )}

                  <div className="mt-auto flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {formatBytes(item.filesize)}
                    </span>
                    <Button
                      onClick={() => handleDownload(item)}
                      data-testid={`download-btn-${item.id}`}
                      size="sm"
                      className="bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {text.download}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
